import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Hand, Clock, Briefcase, Loader2, Check, X, Users, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ShiftBids = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shift-pickup-requests/pending`, { headers: getAuthHeaders() });
      setBids(response.data);
    } catch (error) {
      toast.error("Failed to load shift bids");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (bidId) => {
    setProcessingId(bidId);
    try {
      await axios.put(`${API}/shift-pickup-requests/${bidId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("Bid approved — shift assigned");
      fetchBids();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to approve");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (bidId) => {
    setProcessingId(bidId);
    try {
      await axios.put(`${API}/shift-pickup-requests/${bidId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("Bid rejected");
      fetchBids();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to reject");
    } finally {
      setProcessingId(null);
    }
  };

  // API returns data already grouped by shift with requesters array
  // Transform to expected format: each group has shift info + bidders array
  const groupedList = bids.map(group => ({
    shift_id: group.shift_id,
    event_name: group.event_name || "Unknown Shift",
    date: group.date || "",
    start_time: group.start_time || "",
    end_time: group.end_time || "",
    bidders: group.requesters || [],
  }));
  
  // Count total bids across all groups
  const totalBids = groupedList.reduce((sum, g) => sum + g.bidders.length, 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="shift-bids-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-[#6A1E2E]">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Shift Bids</h1>
          <p className="text-[#666666]">
            {totalBids} pending bid{totalBids !== 1 ? "s" : ""} across {groupedList.length} shift{groupedList.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#6A1E2E]" />
        </div>
      ) : groupedList.length === 0 ? (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardContent className="py-16 text-center">
            <Hand className="w-12 h-12 mx-auto text-[#999999] mb-4" />
            <p className="text-[#666666] text-lg font-medium">No pending shift bids</p>
            <p className="text-[#999999] text-sm mt-1">All bids have been processed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedList.map((group) => (
            <Card key={group.shift_id} className="border border-[#E5E5E5] shadow-none" data-testid={`bid-group-${group.shift_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading text-[#1A1A1A]">{group.event_name}</CardTitle>
                    <div className="flex items-center gap-3 mt-1 text-sm text-[#666666]">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {group.date} | {group.start_time} - {group.end_time}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                    <Users className="w-3 h-3 mr-1" />
                    {group.bidders.length} bid{group.bidders.length !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#666666] mb-2">
                  Bidders (sorted by seniority & hours)
                </p>
                <div className="space-y-2">
                  {group.bidders.map((bid, idx) => (
                    <div
                      key={bid.id}
                      className={`flex items-center justify-between p-3 rounded-sm ${
                        idx === 0 ? "bg-green-50 border border-green-200" : "bg-[#FAFAFA] border border-[#E5E5E5]"
                      }`}
                      data-testid={`bid-item-${bid.id}`}
                    >
                      <div>
                        <span className="text-sm font-medium text-[#1A1A1A]">{bid.user_name}</span>
                        {idx === 0 && (
                          <Badge className="ml-2 bg-green-100 text-green-800 text-xs hover:bg-green-100">Top Seniority</Badge>
                        )}
                        <div className="text-xs text-[#666666] mt-0.5">
                          Hired: {bid.date_of_hire || "N/A"} | Week hours: {bid.scheduled_hours}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(bid.id)}
                          disabled={processingId === bid.id}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs gap-1"
                          data-testid={`approve-${bid.id}`}
                        >
                          {processingId === bid.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3" /> Assign</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(bid.id)}
                          disabled={processingId === bid.id}
                          className="border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs gap-1"
                          data-testid={`reject-${bid.id}`}
                        >
                          <X className="w-3 h-3" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShiftBids;
